// =============================================================================
// screens/LearnScreen.tsx — "Learn with CRYB" (LearnIndex route).
//
// REVAMP (Learn Center): rebuilt to the client-loved reference design —
// Beginner / Intermediate / Expert level pills (selected = light purple pill)
// over a 2-column grid of vivid gradient step cards ("Step 01", "Step 02" …),
// all inside the current lavender/glass palette.
//
// Data contract is UNCHANGED: GET /learn?limit=100 →
// { data: { articles, pagination } }; each article carries the backend's
// difficulty ('beginner' | 'intermediate' | 'advanced' — surfaced as Expert).
// Articles with no difficulty land in Beginner so nothing published is lost.
// Cards still open LearnArticle ({ slug }). No content is invented — a level
// with no items shows a tasteful empty state.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, RefreshControl, useWindowDimensions,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import { colors, spacing, radius, fonts, getLayout } from '../constants/theme';
import { api } from '../services/api';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenHeading from '../components/ui/ScreenHeading';
import Asset3D from '../components/ui/Asset3D';
import StepCard from '../components/learn/StepCard';
import LevelPills from '../components/learn/LevelPills';
import MustSeeRail from '../components/learn/MustSeeRail';
import { LearnLevel, LEARN_LEVELS } from '../components/learn/gradients';
import { tabularNums } from '../lib/format';
import LinearGradient from 'react-native-linear-gradient';
import Animated, { FadeInDown } from 'react-native-reanimated';
import {
  loadProgress, subscribeProgress, getProgress, levelForXp,
  isPassed, type LearnProgress,
} from '../lib/learnProgress';

type Nav = NativeStackNavigationProp<RootStackParamList>;

interface Article {
  id?: string;
  slug?: string;
  title?: string;
  description?: string;
  excerpt?: string;
  category?: string;
  difficulty?: string;
  image?: string;
  imageUrl?: string;
  featured?: boolean;
  featuredAt?: string | null;
  createdAt?: string;
}

// Normalise the backend difficulty to one of the three level buckets.
// Unknown/missing difficulty → beginner (entry point of the course).
function bucketOf(a: Article): LearnLevel {
  const d = (a.difficulty || '').toLowerCase();
  if (d === 'intermediate') return 'intermediate';
  if (d === 'advanced' || d === 'expert') return 'advanced';
  return 'beginner';
}

export default function LearnScreen() {
  const navigation = useNavigation<Nav>();
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: 960, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  // Wide screens fit a 3-up grid of step cards; phones stay 2-up so cards keep a
  // premium, readable size instead of stretching edge-to-edge.
  const numColumns = layout === 'desktop' ? 3 : 2;

  const [articles, setArticles] = useState<Article[]>([]);
  const [level, setLevel] = useState<LearnLevel>('beginner');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Gamification progress (XP, passed lessons) — client store, re-read on focus
  // so passing a quiz inside an article reflects the moment you come back.
  const [progress, setProgress] = useState<LearnProgress>(getProgress());
  useEffect(() => {
    loadProgress();
    return subscribeProgress(setProgress);
  }, []);
  useFocusEffect(useCallback(() => { setProgress(getProgress()); }, []));

  // #58: onboarding-style animated intro — replay the staggered entrance each
  // time the Learning Center gains focus. Bumping the key remounts the Animated
  // wrappers so their `entering` layout animations run again.
  const [introKey, setIntroKey] = useState(0);
  useFocusEffect(useCallback(() => { setIntroKey((k) => k + 1); }, []));

  const fetchArticles = useCallback(async () => {
    try {
      const res = await api<any>('/learn?limit=100');
      const d: any = res.data ?? {};
      const list = d.articles ?? d.items ?? (Array.isArray(d) ? d : []);
      setArticles(Array.isArray(list) ? list : []);
      setLoadFailed(!res.success);
    } catch {
      setArticles([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchArticles();
    setRefreshing(false);
  }, [fetchArticles]);

  // Bucket once per fetch; steps within a level run oldest → newest so the
  // numbering reads as a course (Step 01 = the level's first lesson).
  const byLevel = useMemo(() => {
    const buckets: Record<LearnLevel, Article[]> = {
      beginner: [], intermediate: [], advanced: [],
    };
    for (const a of articles) buckets[bucketOf(a)].push(a);
    const time = (a: Article) => {
      const t = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
      return isNaN(t) ? 0 : t;
    };
    (Object.keys(buckets) as LearnLevel[]).forEach((k) => {
      buckets[k] = [...buckets[k]].sort((a, b) => time(a) - time(b));
    });
    return buckets;
  }, [articles]);

  const counts = useMemo(() => ({
    beginner: byLevel.beginner.length,
    intermediate: byLevel.intermediate.length,
    advanced: byLevel.advanced.length,
  }), [byLevel]);

  // XP → level for the header progress bar.
  const xp = progress.xp;
  const { level: playerLevel, intoLevel, perLevel } = levelForXp(xp);
  const passedCount = useMemo(
    () => Object.values(progress.passed).filter(Boolean).length,
    [progress.passed],
  );
  const totalLessons = counts.beginner + counts.intermediate + counts.advanced;

  // Admin-curated "Must-see" rail — most recently featured first. Comes from
  // the same fetch (featured is on every article), so no extra request.
  const mustSee = useMemo(() => {
    const time = (a: Article) => {
      const t = a.featuredAt ? new Date(a.featuredAt).getTime() : NaN;
      return isNaN(t) ? 0 : t;
    };
    return articles.filter((a) => a.featured && a.slug).sort((a, b) => time(b) - time(a));
  }, [articles]);

  const steps = byLevel[level];
  const levelName = LEARN_LEVELS.find((l) => l.value === level)?.label || 'Beginner';

  const Header = (
    <View style={[styles.intro, { paddingTop: insets.top + spacing.md }]}>
      <Animated.View key={`h-${introKey}`} entering={FadeInDown.duration(480)}>
        <ScreenHeading
          kicker="Learning Center"
          title="Learn with CRYB"
          subtitle="Step-by-step lessons — take the quiz to earn XP (optional)."
        />
      </Animated.View>

      {/* XP / level progress — the "game" header. */}
      <Animated.View key={`xp-${introKey}`} entering={FadeInDown.duration(480).delay(90)}>
      <LinearGradient
        colors={[...colors.gradient]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.xpCard}
      >
        <View style={styles.xpTopRow}>
          <View style={styles.xpLevelBadge}>
            <Ionicons name="flash" size={13} color="#fff" />
            <Text style={styles.xpLevelText}>Level {playerLevel}</Text>
          </View>
          <Text style={styles.xpCount} numberOfLines={1}>
            {passedCount}/{totalLessons || 0} lessons · {xp} XP
          </Text>
        </View>
        <View style={styles.xpTrack}>
          <View style={[styles.xpFill, { width: `${Math.round((intoLevel / perLevel) * 100)}%` }]} />
        </View>
        <Text style={styles.xpHint}>
          {perLevel - intoLevel} XP to level {playerLevel + 1}
        </Text>
      </LinearGradient>
      </Animated.View>

      {mustSee.length > 0 && (
        <Animated.View key={`ms-${introKey}`} entering={FadeInDown.duration(480).delay(180)}>
          <MustSeeRail
            articles={mustSee}
            onPressArticle={(slug) => navigation.navigate('LearnArticle', { slug })}
            bleed={spacing.lg}
            style={styles.mustSee}
          />
        </Animated.View>
      )}
      <Animated.View key={`lp-${introKey}`} entering={FadeInDown.duration(480).delay(mustSee.length > 0 ? 260 : 180)} style={styles.pillsWrap}>
        <LevelPills selected={level} onSelect={setLevel} counts={counts} />
      </Animated.View>
    </View>
  );

  if (loading) {
    return (
      <View style={[styles.screen, styles.center]}>
        <ParticleBackground />
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Lessons are intentionally NEVER locked — the end-of-article quiz is optional
  // (client ask) and earns XP only, it does not gate progression. So the grid is
  // always the full set of steps for the selected level (no unlock filtering).
  const baseGrid = steps;
  // Pad to a full last row with invisible spacers so an odd final card stays a
  // half-column (flex:1 alone in a row would stretch it full-width — the reported
  // "misaligned" learn card).
  const rem = baseGrid.length % numColumns;
  const gridData = rem === 0
    ? baseGrid
    : [...baseGrid, ...Array.from({ length: numColumns - rem }, (_, i) => ({ __spacer: true, id: `spacer-${i}` } as any))];

  return (
    <View style={styles.screen}>
      <ParticleBackground />
      <FlatList
        key={`${level}-${numColumns}`} // reset the grid when level OR column count changes
        data={gridData}
        numColumns={numColumns}
        keyExtractor={(item, i) => item.slug ?? item.id ?? String(i)}
        ListHeaderComponent={Header}
        columnWrapperStyle={styles.columnWrap}
        renderItem={({ item, index }) => {
          // Invisible spacer to keep the odd last card at half width.
          if ((item as any).__spacer) return <View style={styles.gridCard} />;
          const passed = isPassed(item.slug);
          return (
            <StepCard
              article={item}
              index={index}
              style={styles.gridCard}
              passed={passed}
              onPress={() => {
                if (item.slug) navigation.navigate('LearnArticle', { slug: item.slug });
              }}
            />
          );
        }}
        contentContainerStyle={[styles.listContent, containerStyle]}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Asset3D name={loadFailed ? 'error' : 'learn'} size={128} style={{ marginBottom: spacing.sm }} />
            <Text style={styles.emptyText}>
              {loadFailed ? "Couldn't load lessons" : `No ${levelName} lessons yet`}
            </Text>
            <Text style={styles.emptySub}>
              {loadFailed
                ? 'Check your connection and try again.'
                : 'New lessons for this level are on the way — check back soon.'}
            </Text>
            {loadFailed && (
              <TouchableOpacity
                style={styles.retryBtn}
                onPress={() => { setLoading(true); fetchArticles(); }}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel="Retry loading lessons"
              >
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            )}
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  center: { justifyContent: 'center', alignItems: 'center' },
  listContent: { padding: spacing.lg, paddingBottom: spacing.xxxl },
  intro: { marginBottom: spacing.lg },
  kicker: {
    ...fonts.bold, fontSize: 11, color: colors.primary,
    letterSpacing: 1.2, marginBottom: spacing.xs,
  },
  introTitle: { ...fonts.extrabold, fontSize: 28, color: colors.ink },
  introSub: {
    ...fonts.regular, fontSize: 14, color: colors.muted,
    marginTop: spacing.xs, lineHeight: 20,
  },
  mustSee: { marginTop: spacing.xl },
  pillsWrap: { marginTop: spacing.lg },
  xpCard: {
    width: '100%',           // bound the gradient to the row so the XP count truncates instead of clipping past the right edge
    marginTop: spacing.lg,
    borderRadius: radius.lg,
    padding: spacing.md,
  },
  xpTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  xpLevelBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderRadius: radius.full, paddingHorizontal: spacing.sm, paddingVertical: 3,
  },
  xpLevelText: { ...fonts.extrabold, fontSize: 12, color: '#FFFFFF', ...tabularNums },
  xpCount: { ...fonts.semibold, fontSize: 12, color: 'rgba(255,255,255,0.92)', ...tabularNums, flex: 1, minWidth: 0, textAlign: 'right', marginLeft: spacing.sm },
  xpTrack: {
    height: 8, borderRadius: 4, marginTop: spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.25)', overflow: 'hidden',
  },
  xpFill: { height: 8, borderRadius: 4, backgroundColor: '#FFFFFF' },
  xpHint: { ...fonts.medium, fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 6, ...tabularNums },
  columnWrap: { gap: spacing.md },
  gridCard: { flex: 1, marginBottom: spacing.md },
  empty: { alignItems: 'center', paddingTop: 48, paddingHorizontal: spacing.xxxl },
  emptyGlyph: {
    width: 64, height: 64, borderRadius: 20, backgroundColor: colors.primary + '15',
    alignItems: 'center', justifyContent: 'center',
  },
  emptyText: { ...fonts.semibold, fontSize: 17, color: colors.ink, marginTop: spacing.lg },
  emptySub: {
    ...fonts.regular, fontSize: 14, color: colors.muted, textAlign: 'center',
    marginTop: spacing.sm, lineHeight: 20,
  },
  retryBtn: {
    marginTop: spacing.lg,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingHorizontal: spacing.xxl,
    paddingVertical: spacing.sm + 2,
  },
  retryText: { ...fonts.semibold, fontSize: 14, color: colors.white },
});
